const LOCK_NAME = 'casino_active_tab';

/**
 * Module-level controller for the currently-held lock in this browsing context.
 * Used to distinguish same-tab re-acquisition (HMR) from cross-tab steal.
 */
let activeController: AbortController | null = null;

export class MultiTabService {
  private _onLockStolenCallback: (() => void) | null = null;
  private _lockHeld = false;

  /**
   * Register a callback invoked when another tab steals the lock
   * (the user opened the app in a new tab or clicked "Use Here").
   */
  public init(onLockStolen: () => void): void {
    this._onLockStolenCallback = onLockStolen;

    // ios 26+ fix, to avoid new ios page caching system
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this._lockHeld) {
        window.$app.logger.log('BFCache restore detected — re-acquiring active-tab lock');
        this._lockHeld = false;
        this.activateTab();
      }
    });
  }

  /**
   * Become the active tab by acquiring an exclusive Web Lock with `steal`.
   *
   * The most recently activated tab always wins — any previously-holding
   * tab is deposed and redirected to `tab-locked.html`.
   */
  public activateTab(): Promise<boolean> {
    if (!navigator.locks) {
      window.$app.logger.log('Web Locks API unavailable — skipping multi-tab enforcement');
      return Promise.resolve(true);
    }

    if (this._lockHeld) {
      return Promise.resolve(true);
    }

    // Mark any previous lock in this tab as intentionally released (HMR)
    const previousController = activeController;
    if (previousController) {
      previousController.abort();
    }
    activeController = null;

    const controller = new AbortController();

    window.$app.logger.log('acquiring active-tab lock');

    return new Promise<boolean>((resolveActivation) => {
      navigator.locks
        .request(LOCK_NAME, { steal: true }, () => {
          this._lockHeld = true;
          activeController = controller;
          window.$app.logger.log('active-tab lock acquired');
          resolveActivation(true);

          // Hold the lock indefinitely — never resolves on its own
          return new Promise<void>(() => {});
        })
        .catch(() => {
          // Lock was stolen: navigator.locks.request rejects with AbortError.
          // Only lock the tab if this was a cross-tab steal (not same-tab HMR).
          if (controller === activeController) {
            this._lockHeld = false;
            activeController = null;
            window.$app.logger.log('active-tab lock stolen by another tab');
            this._onLockStolenCallback?.();
          }
        });
    });
  }
}
